import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { UserAvatar } from "@/components/UserAvatar";
import {
  AreaChart,
  Area,
} from "recharts";
import {
  FileVideo,
  Share2,
  Wallet,
  Megaphone,
  Users,
  RefreshCw,
  Check,
  X,
  Trash2,
  Eye,
  Flag,
  Send,
  ArrowRightLeft,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  Sparkles,
  ClipboardCheck,
  ExternalLink,
} from "lucide-react";

const MOD_PAGES = ["dashboard", "submissions", "socials", "users", "wallet", "templates", "bulk", "announcements"];

const pageMeta = {
  dashboard: { label: "Dashboard", icon: LayoutDashboard },
  submissions: { label: "Submissions", icon: FileVideo },
  socials: { label: "Socials", icon: Share2 },
  users: { label: "Users", icon: Users },
  wallet: { label: "Wallet", icon: Wallet },
  templates: { label: "Templates", icon: Sparkles },
  bulk: { label: "Bulk Review", icon: ClipboardCheck },
  announcements: { label: "Announce", icon: Megaphone },
};

function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    accepted: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
    flagged: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
    paid: "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300",
    approved: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
    rejected: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
  };
  return (
    <Badge className={`${map[status] || "bg-muted text-foreground"} hover:opacity-80`}>
      {status}
    </Badge>
  );
}

function displayName(user) {
  return user?.discord_username || user?.username || user?.email || "Unknown";
}

function dayKey(value) {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function makeTrend(items = [], valueKey = null) {
  const buckets = new Map();
  items.forEach((item) => {
    const key = dayKey(item.created_at || item.updated_at);
    const current = buckets.get(key) || 0;
    buckets.set(key, current + (valueKey ? Number(item[valueKey] || 0) : 1));
  });
  const rows = Array.from(buckets.entries()).slice(-7).map(([date, value]) => ({ date, value }));
  return rows.length ? rows : [{ date: "Now", value: 0 }];
}

function TrendCard({ title, value, data, testId, tone = "chartreuse" }) {
  const stroke = tone === "amber" ? "#fbbf24" : tone === "rose" ? "#fb7185" : "#c8ff00";
  const gradientId = `${testId}-gradient`.replace(/[^a-zA-Z0-9_-]/g, "");
  return (
    <div data-testid={testId} className="rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a] p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div data-testid={`${testId}-title`} className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{title}</div>
          <div data-testid={`${testId}-value`} className="mt-1 text-2xl font-black text-white">{value}</div>
        </div>
        <div className="h-12 w-28 min-w-[112px] overflow-hidden">
            <AreaChart width={112} height={48} data={data} margin={{ top: 3, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={stroke} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={stroke} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="value" stroke={stroke} fill={`url(#${gradientId})`} strokeWidth={2} dot={false} />
            </AreaChart>
        </div>
      </div>
    </div>
  );
}

function LineTrendSvg({ data, testId }) {
  const rows = data?.length ? data : [{ date: "Now", value: 0 }];
  const max = Math.max(...rows.map((row) => Number(row.value || 0)), 1);
  const denominator = Math.max(rows.length - 1, 1);
  const points = rows.map((row, index) => {
    const x = (index / denominator) * 100;
    const y = 88 - (Number(row.value || 0) / max) * 68;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(" ");
  return (
    <div data-testid={testId} className="relative h-full min-h-[220px] overflow-hidden rounded-xl border border-[#1a1a1a] bg-black p-3">
      <svg data-testid={`${testId}-svg`} viewBox="0 0 100 100" preserveAspectRatio="none" className="h-full w-full">
        <defs>
          <linearGradient id="modLineTrendFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#c8ff00" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#c8ff00" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polyline points={`0,96 ${points} 100,96`} fill="url(#modLineTrendFill)" stroke="none" />
        <polyline points={points} fill="none" stroke="#c8ff00" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        {rows.map((row, index) => {
          const [x, y] = points.split(" ")[index].split(",");
          return <circle key={`${row.date}-${index}`} cx={x} cy={y} r="1.8" fill="#0a0a0a" stroke="#c8ff00" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />;
        })}
      </svg>
      <div className="pointer-events-none absolute inset-x-3 bottom-2 flex justify-between text-[10px] text-zinc-600">
        {rows.slice(0, 7).map((row, index) => <span data-testid={`${testId}-label-${index}`} key={`${row.date}-${index}`}>{row.date}</span>)}
      </div>
    </div>
  );
}

function UserMention({ user, name, avatar, testId, meta }) {
  const merged = user || { discord_username: name, avatar_url: avatar };
  return (
    <div data-testid={testId} className="flex min-w-0 items-center gap-3">
      <UserAvatar user={merged} name={name} src={avatar} size={42} testId={`${testId}-avatar`} />
      <div className="min-w-0">
        <div data-testid={`${testId}-name`} className="truncate text-sm font-bold text-white">{name || displayName(merged)}</div>
        {meta && <div data-testid={`${testId}-meta`} className="truncate text-xs text-zinc-500">{meta}</div>}
      </div>
    </div>
  );
}

export function ModPanelPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { modTab } = useParams();

  const [me, setMe] = useState(null);
  const page = MOD_PAGES.includes(modTab) ? modTab : "dashboard";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Submissions
  const [submissions, setSubmissions] = useState([]);
  const [subStatus, setSubStatus] = useState("all");

  // Social requests
  const [socialRequests, setSocialRequests] = useState([]);
  const [socialStatus, setSocialStatus] = useState("all");

  // Users (loaded globally for all tabs)
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [selectedUserDashboard, setSelectedUserDashboard] = useState(null);

  // Wallet tools
  const [walletUserId, setWalletUserId] = useState("");
  const [walletAmount, setWalletAmount] = useState("");
  const [walletNote, setWalletNote] = useState("");
  const [walletType, setWalletType] = useState("deposit");
  const [walletTag, setWalletTag] = useState("");
  const [modTxLogs, setModTxLogs] = useState([]);

  // Announcements
  const [announcements, setAnnouncements] = useState([]);
  const [announceTitle, setAnnounceTitle] = useState("");
  const [announceMessage, setAnnounceMessage] = useState("");

  // Moderation templates + bulk tools
  const [templates, setTemplates] = useState([]);
  const [templateTitle, setTemplateTitle] = useState("");
  const [templateReason, setTemplateReason] = useState("");
  const [templateMessage, setTemplateMessage] = useState("");
  const [selectedSubmissionIds, setSelectedSubmissionIds] = useState([]);

  const roleAccess = me?.role_access || [];
  const hasAccess = (pages = [], permissions = []) => {
    if (!roleAccess.length) return false;
    return roleAccess.some((role) => {
      if (role?.status === "disabled") return false;
      const rolePages = role?.pages || [];
      const rolePerms = role?.permissions || [];
      const pageMatch = !pages.length || pages.some((p) => rolePages.includes(p));
      const permMatch = !permissions.length || permissions.some((p) => rolePerms.includes(p));
      return pageMatch && permMatch;
    });
  };
  const modPageAccess = {
    dashboard: { pages: ["mod_dashboard"], permissions: ["review_submissions"] },
    submissions: { pages: ["mod_submissions"], permissions: ["review_submissions"] },
    socials: { pages: ["mod_socials"], permissions: ["review_submissions"] },
    users: { pages: ["mod_users"], permissions: ["review_submissions", "view_creator_insights"] },
    wallet: { pages: ["mod_wallet"], permissions: ["manage_payouts"] },
    templates: { pages: ["mod_templates"], permissions: ["review_submissions"] },
    bulk: { pages: ["mod_bulk"], permissions: ["review_submissions"] },
    announcements: { pages: ["mod_announcements"], permissions: ["review_submissions"] },
  };
  const accessConfig = modPageAccess[page];
  const canAccessPage = accessConfig ? hasAccess(accessConfig.pages, accessConfig.permissions) : false;

  const deny = useMemo(() => {
    if (!me) return "Not logged in";
    if (!canAccessPage) return "You don't have access to this Mod page.";
    return null;
  }, [me, canAccessPage]);

  const pendingSubs = submissions.filter((s) => s.status === "pending").length;
  const acceptedSubs = submissions.filter((s) => s.status === "accepted" || s.status === "paid").length;
  const flaggedSubs = submissions.filter((s) => s.status === "flagged").length;
  const pendingSocials = socialRequests.filter((s) => s.status === "pending").length;
  const submissionTrend = useMemo(() => makeTrend(submissions), [submissions]);
  const viewTrend = useMemo(() => makeTrend(submissions, "views"), [submissions]);
  const socialTrend = useMemo(() => makeTrend(socialRequests), [socialRequests]);

  const selectedWalletUser = useMemo(() => {
    if (!walletUserId) return null;
    return (users || []).find((u) => u.id === walletUserId) || null;
  }, [users, walletUserId]);

  async function loadModTxLogs(uid) {
    try {
      const r = await api.get(`/admin/wallet/${uid}/transactions`);
      setModTxLogs(r.data?.transactions || []);
    } catch { setModTxLogs([]); }
  }


  const selectedUserIndex = useMemo(() => {
    if (!selectedUserId || !users.length) return -1;
    return users.findIndex((u) => u.id === selectedUserId);
  }, [users, selectedUserId]);

  function goToPrevUser() {
    if (selectedUserIndex <= 0) return;
    loadSelectedUser(users[selectedUserIndex - 1].id);
  }

  function goToNextUser() {
    if (selectedUserIndex < 0 || selectedUserIndex >= users.length - 1) return;
    loadSelectedUser(users[selectedUserIndex + 1].id);
  }

  async function loadMe() {
    const res = await api.get("/me");
    setMe(res.data);
  }

  async function loadSubmissions() {
    const params = {};
    if (subStatus && subStatus !== "all") params.status = subStatus;
    const res = await api.get("/admin/submissions", { params });
    setSubmissions(res.data?.items || []);
  }

  async function loadSocialRequests() {
    const params = {};
    if (socialStatus && socialStatus !== "all") params.status = socialStatus;
    const res = await api.get("/admin/social_requests", { params });
    setSocialRequests(res.data?.items || []);
  }

  async function loadUsers() {
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data?.items || []);
    } catch {
      setUsers([]);
    }
  }

  async function loadSelectedUser(id) {
    setSelectedUserId(id);
    setSelectedUserProfile(null);
    setSelectedUserDashboard(null);
    try {
      const [profileRes, dashRes] = await Promise.all([
        api.get(`/admin/users/${id}/profile`),
        api.get(`/admin/users/${id}/dashboard`),
      ]);
      setSelectedUserProfile(profileRes.data);
      setSelectedUserDashboard(dashRes.data);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  async function loadAnnouncements() {
    const res = await api.get("/admin/announcements");
    setAnnouncements(res.data?.items || []);
  }

  async function loadTemplates() {
    const res = await api.get("/admin/rejection_templates");
    setTemplates(res.data?.items || []);
  }

  async function createTemplate() {
    if (!templateTitle.trim() || !templateReason.trim()) return;
    try {
      await api.post("/admin/rejection_templates", {
        title: templateTitle.trim(),
        primary_reason: templateReason.trim(),
        secondary_reasons: [],
        message: templateMessage.trim(),
      });
      setTemplateTitle("");
      setTemplateReason("");
      setTemplateMessage("");
      await loadTemplates();
      toast({ title: "Template saved" });
    } catch (e) {
      toast({ title: "Error", description: apiErrorMessage(e) });
    }
  }

  async function setSubmissionStatus(id, status) {
    await api.put(`/admin/submissions/${id}/status`, { status });
  }

  async function bulkUpdate(status) {
    if (!selectedSubmissionIds.length) return;
    setBusy(true);
    try {
      await Promise.all(selectedSubmissionIds.map((id) => setSubmissionStatus(id, status)));
      setSelectedSubmissionIds([]);
      await loadSubmissions();
      toast({ title: "Bulk review complete", description: `${selectedSubmissionIds.length} submissions updated.` });
    } catch (e) {
      toast({ title: "Error", description: apiErrorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  async function bulkDelete() {
    if (!selectedSubmissionIds.length || !window.confirm("Delete selected submissions?")) return;
    setBusy(true);
    try {
      await Promise.all(selectedSubmissionIds.map((id) => api.delete(`/admin/submissions/${id}`)));
      setSelectedSubmissionIds([]);
      await loadSubmissions();
      toast({ title: "Selected submissions deleted" });
    } catch (e) {
      toast({ title: "Error", description: apiErrorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  // Load me + all users on mount
  useEffect(() => {
    (async () => {
      try {
        setBusy(true);
        await loadMe();
      } catch (e) {
        setError(apiErrorMessage(e));
      } finally {
        setBusy(false);
      }
    })();
  }, []);

  // Load users once after auth
  useEffect(() => {
    if (deny) return;
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deny]);

  // Load page-specific data
  useEffect(() => {
    if (deny) return;
    (async () => {
      try {
        setError(null);
        if (page === "dashboard") await Promise.all([loadSubmissions(), loadSocialRequests(), loadUsers(), loadAnnouncements(), loadTemplates()]);
        if (page === "submissions" || page === "bulk") await loadSubmissions();
        if (page === "socials") await loadSocialRequests();
        if (page === "users" || page === "wallet" || page === "announcements") await loadUsers();
        if (page === "templates") await loadTemplates();
        if (page === "announcements") await loadAnnouncements();
      } catch (e) {
        setError(apiErrorMessage(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, subStatus, socialStatus, deny]);

  useEffect(() => {
    if (!modTab || !MOD_PAGES.includes(modTab)) navigate("/mod/dashboard", { replace: true });
  }, [modTab, navigate]);

  if (deny) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <Card className="w-full max-w-md border-border/60 bg-card/70">
          <CardHeader><CardTitle className="text-base">Mod Panel</CardTitle></CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">{deny}</div>
            {error && <div className="mt-2 text-sm text-rose-400">{error}</div>}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="mod-panel">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
        <div>
          <h1 data-testid="mod-panel-title" className="page-title">Mod Panel</h1>
          <p className="page-subtitle">
            Manage submissions, socials, users, wallets, templates &amp; announcements.
          </p>
        </div>
        <Button variant="secondary" size="sm" className="w-fit rounded-full" data-testid="mod-open-campaigns" onClick={() => navigate("/campaigns")}>
          Open Campaigns
        </Button>
      </div>

      <div data-testid="mod-page-nav" className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8">
        {MOD_PAGES.map((key) => {
          const Icon = pageMeta[key].icon;
          return (
            <button
              key={key}
              data-testid={`mod-page-link-${key}`}
              className={`flex items-center gap-2 rounded-2xl border px-3 py-3 text-left text-xs font-bold transition-transform hover:-translate-y-0.5 ${page === key ? "border-[#c8ff00]/40 bg-[#c8ff00]/10 text-[#c8ff00]" : "border-[#1a1a1a] bg-[#0a0a0a] text-zinc-500 hover:text-white"}`}
              onClick={() => navigate(`/mod/${key}`)}
            >
              <Icon size={15} />
              <span>{pageMeta[key].label}</span>
            </button>
          );
        })}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        <div data-testid="mod-stat-role" className="rounded-xl border border-border/60 bg-card/70 p-3 sm:p-4">
          <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground sm:text-xs">Role</div>
          <div className="mt-0.5 text-base font-semibold capitalize sm:text-lg">{me?.role || "-"}</div>
        </div>
        <div data-testid="mod-stat-session" className="rounded-xl border border-border/60 bg-card/70 p-3 sm:p-4">
          <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground sm:text-xs">Session</div>
          <div className="mt-0.5 truncate text-base font-semibold sm:text-lg">{me?.discord_username || me?.username || "-"}</div>
        </div>
        <div data-testid="mod-stat-pending-subs" className="rounded-xl border border-violet-500/20 bg-violet-500/[0.04] p-3 sm:p-4">
          <div className="text-[10px] font-medium uppercase tracking-wider text-violet-700 dark:text-violet-400 sm:text-xs">Pending Subs</div>
          <div className="mt-0.5 text-base font-semibold sm:text-lg">{pendingSubs}</div>
        </div>
        <div data-testid="mod-stat-pending-socials" className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-3 sm:p-4">
          <div className="text-[10px] font-medium uppercase tracking-wider text-amber-700 dark:text-amber-400 sm:text-xs">Pending Socials</div>
          <div className="mt-0.5 text-base font-semibold sm:text-lg">{pendingSocials}</div>
        </div>
      </div>

      {error && (
        <div data-testid="mod-error" className="rounded-xl border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-xs text-rose-300 sm:px-4 sm:py-3 sm:text-sm">
          {error}
        </div>
      )}

      {page === "dashboard" && (
        <div data-testid="mod-dashboard-page" className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <TrendCard title="Pending Subs" value={pendingSubs} data={submissionTrend} testId="mod-trend-pending-subs" tone="amber" />
            <TrendCard title="Accepted Clips" value={acceptedSubs} data={viewTrend} testId="mod-trend-accepted-clips" />
            <TrendCard title="Flagged Clips" value={flaggedSubs} data={submissionTrend} testId="mod-trend-flagged-clips" tone="rose" />
            <TrendCard title="Social Queue" value={pendingSocials} data={socialTrend} testId="mod-trend-socials" />
          </div>
          <Card className="border-[#1a1a1a] bg-[#0a0a0a]">
            <CardHeader><CardTitle className="text-sm">Moderation Line Trends</CardTitle></CardHeader>
            <CardContent className="h-[260px] min-w-0">
              <LineTrendSvg data={submissionTrend} testId="mod-dashboard-line-trend" />
            </CardContent>
          </Card>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="border-[#1a1a1a] bg-[#0a0a0a]"><CardHeader><CardTitle className="text-sm">Latest Submissions</CardTitle></CardHeader><CardContent className="space-y-2">{submissions.slice(0, 5).map((s) => <div key={s.id} data-testid={`mod-dashboard-sub-${s.id}`} className="flex items-center gap-3 rounded-xl border border-[#1a1a1a] bg-black p-3"><StatusBadge status={s.status} /><UserMention testId={`mod-dashboard-sub-user-${s.id}`} name={s.clipper_username || s.clipper_tag} avatar={s.clipper_avatar} meta={s.campaign_name} /><div className="ml-auto text-xs font-bold text-[#c8ff00]">{Number(s.views || 0).toLocaleString()} views</div></div>)}{!submissions.length && <div data-testid="mod-dashboard-sub-empty" className="py-8 text-center text-sm text-zinc-500">No submissions yet.</div>}</CardContent></Card>
            <Card className="border-[#1a1a1a] bg-[#0a0a0a]"><CardHeader><CardTitle className="text-sm">Latest Users</CardTitle></CardHeader><CardContent className="space-y-2">{users.slice(0, 5).map((u) => <div key={u.id} data-testid={`mod-dashboard-user-${u.id}`} className="flex items-center gap-3 rounded-xl border border-[#1a1a1a] bg-black p-3"><UserMention testId={`mod-dashboard-user-mention-${u.id}`} user={u} meta={`Wallet $${Number(u.wallet_available || 0).toFixed(2)}`} /><Button data-testid={`mod-dashboard-user-view-${u.id}`} size="sm" variant="secondary" className="ml-auto rounded-full" onClick={() => { loadSelectedUser(u.id); navigate("/mod/users"); }}>View</Button></div>)}{!users.length && <div data-testid="mod-dashboard-users-empty" className="py-8 text-center text-sm text-zinc-500">No users yet.</div>}</CardContent></Card>
          </div>
        </div>
      )}

      {page === "submissions" && (
          <Card className="border-border/60 bg-card/70">
            <CardHeader className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3">
              <CardTitle className="text-sm sm:text-base">Submission Queue</CardTitle>
              <div className="flex items-center gap-2">
                <Select value={subStatus} onValueChange={setSubStatus}>
                  <SelectTrigger data-testid="mod-sub-filter" className="h-8 w-[120px] border-border/60 bg-background text-xs sm:h-9 sm:w-[140px] sm:text-sm">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="accepted">Accepted</SelectItem>
                    <SelectItem value="flagged">Flagged</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="secondary" size="sm" className="h-8 gap-1 rounded-full text-xs sm:h-9" data-testid="mod-sub-refresh" onClick={loadSubmissions}>
                  <RefreshCw size={12} /> Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                <Table data-testid="mod-sub-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Clipper</TableHead>
                      <TableHead className="text-xs">Campaign</TableHead>
                      <TableHead className="hidden text-xs sm:table-cell">Platform</TableHead>
                      <TableHead className="text-xs">Status</TableHead>
                      <TableHead className="text-right text-xs">Views</TableHead>
                      <TableHead className="text-right text-xs">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(submissions || []).map((s) => (
                      <TableRow key={s.id} data-testid={`mod-sub-row-${s.id}`}>
                        <TableCell className="min-w-[180px] text-xs font-medium sm:text-sm"><UserMention testId={`mod-sub-user-${s.id}`} name={s.clipper_username || s.clipper_tag} avatar={s.clipper_avatar} meta={s.clipper_discord_id ? `...${String(s.clipper_discord_id).slice(-4)}` : "Clipper"} /></TableCell>
                        <TableCell className="max-w-[100px] truncate text-xs text-muted-foreground sm:max-w-[140px] sm:text-sm">{s.campaign_name || "-"}</TableCell>
                        <TableCell className="hidden text-xs sm:table-cell sm:text-sm">{s.platform}</TableCell>
                        <TableCell><StatusBadge status={s.status} /></TableCell>
                        <TableCell className="text-right font-mono text-xs sm:text-sm">{(s.views || 0).toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <a data-testid={`mod-sub-open-${s.id}`} href={s.video_url} target="_blank" rel="noreferrer" className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[#c8ff00] hover:bg-[#c8ff00]/10 sm:h-8 sm:w-8" title="Open clip"><ExternalLink size={13} /></a>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground sm:h-8 sm:w-8" data-testid={`mod-sub-views-${s.id}`} title="Set views"
                              onClick={async () => { const v = window.prompt("Set views", String(s.views || 0)); if (v == null) return; try { await api.put(`/admin/submissions/${s.id}/views`, { views: Number(v) }); toast({ title: "Updated" }); await loadSubmissions(); } catch (e) { toast({ title: "Error", description: apiErrorMessage(e) }); } }}>
                              <Eye size={13} />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 sm:h-8 sm:w-8" data-testid={`mod-sub-accept-${s.id}`} title="Accept"
                              onClick={async () => { try { await setSubmissionStatus(s.id, "accepted"); await loadSubmissions(); } catch (e) { toast({ title: "Error", description: apiErrorMessage(e) }); } }}>
                              <Check size={13} />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 sm:h-8 sm:w-8" data-testid={`mod-sub-flag-${s.id}`} title="Flag"
                              onClick={async () => { try { await setSubmissionStatus(s.id, "flagged"); await loadSubmissions(); } catch (e) { toast({ title: "Error", description: apiErrorMessage(e) }); } }}>
                              <Flag size={13} />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600 dark:text-rose-400 hover:bg-rose-500/10 sm:h-8 sm:w-8" data-testid={`mod-sub-delete-${s.id}`} title="Delete"
                              onClick={async () => { if (!window.confirm("Delete?")) return; try { await api.delete(`/admin/submissions/${s.id}`); await loadSubmissions(); } catch (e) { toast({ title: "Error", description: apiErrorMessage(e) }); } }}>
                              <Trash2 size={13} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {!submissions?.length && <div data-testid="mod-sub-empty" className="py-6 text-center text-xs text-muted-foreground sm:py-8 sm:text-sm">No submissions found.</div>}
            </CardContent>
          </Card>
      )}

      {page === "socials" && (
          <Card className="border-border/60 bg-card/70">
            <CardHeader className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3">
              <CardTitle className="text-sm sm:text-base">Social Request Queue</CardTitle>
              <div className="flex items-center gap-2">
                <Select value={socialStatus} onValueChange={setSocialStatus}>
                  <SelectTrigger data-testid="mod-social-filter" className="h-8 w-[120px] border-border/60 bg-background text-xs sm:h-9 sm:w-[140px] sm:text-sm">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="secondary" size="sm" className="h-8 gap-1 rounded-full text-xs sm:h-9" data-testid="mod-social-refresh" onClick={loadSocialRequests}>
                  <RefreshCw size={12} /> Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 sm:space-y-3">
              {(socialRequests || []).map((r) => (
                <div key={r.id} data-testid={`mod-social-row-${r.id}`} className="rounded-xl border border-border/60 bg-background p-3 transition-colors hover:bg-muted/30 sm:p-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                    <div className="min-w-0 flex-1">
                      <UserMention testId={`mod-social-user-${r.id}`} name={r.clipper_username || r.clipper_tag} avatar={r.clipper_avatar} meta="Social owner" />
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-xs font-semibold sm:text-sm">{r.platform || "-"}</span>
                        <StatusBadge status={r.status} />
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground sm:text-sm">{r.handle || r.label || "-"}</div>
                      <div className="text-[10px] text-muted-foreground sm:text-xs">{r.clipper_tag || "-"}</div>
                    </div>
                    <div className="flex gap-1.5">
                      <Button size="sm" className="h-8 gap-1 rounded-full bg-emerald-500/15 text-xs text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/20" data-testid={`mod-social-approve-${r.id}`}
                        onClick={async () => { try { await api.put(`/admin/social_requests/${r.id}`, { status: "approved", reason: "" }); await loadSocialRequests(); } catch (e) { toast({ title: "Error", description: apiErrorMessage(e) }); } }}>
                        <Check size={12} /> Approve
                      </Button>
                      <Button size="sm" variant="secondary" className="h-8 gap-1 rounded-full bg-rose-500/15 text-xs text-rose-700 dark:text-rose-300 hover:bg-rose-500/20" data-testid={`mod-social-reject-${r.id}`}
                        onClick={async () => { const reason = window.prompt("Reject reason", ""); if (reason == null) return; try { await api.put(`/admin/social_requests/${r.id}`, { status: "rejected", reason: reason || "" }); await loadSocialRequests(); } catch (e) { toast({ title: "Error", description: apiErrorMessage(e) }); } }}>
                        <X size={12} /> Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {!socialRequests?.length && <div data-testid="mod-social-empty" className="py-6 text-center text-xs text-muted-foreground sm:py-8 sm:text-sm">No social requests found.</div>}
            </CardContent>
          </Card>
      )}

      {page === "users" && (
          <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
            {/* Users list */}
            <Card className="border-border/60 bg-card/70">
              <CardHeader className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3">
                <CardTitle className="text-sm sm:text-base">All Users ({users.length})</CardTitle>
                <Button variant="secondary" size="sm" className="h-8 w-fit gap-1 rounded-full text-xs sm:h-9" data-testid="mod-users-refresh" onClick={loadUsers}>
                  <RefreshCw size={12} /> Refresh
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                  {users.map((u) => (
                    <div
                      key={u.id}
                      data-testid={`mod-user-row-${u.id}`}
                      className={`rounded-2xl border p-4 transition-colors ${
                        selectedUserId === u.id
                          ? "border-[#c8ff00]/40 bg-[#c8ff00]/[0.06]"
                          : "border-border/60 bg-background hover:bg-muted/30"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <UserAvatar user={u} size={54} testId={`mod-user-avatar-${u.id}`} />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-sm font-bold sm:text-base">{displayName(u)}</span>
                            <Badge className="bg-muted text-[10px] text-foreground hover:bg-muted/80">{u.role}</Badge>
                            {u.banned && <Badge className="bg-rose-500/15 text-[10px] text-rose-300">banned</Badge>}
                          </div>
                          <div className="mt-0.5 text-[10px] text-muted-foreground sm:text-xs">
                            Wallet: <span className="font-mono">${Number(u.wallet_available || 0).toFixed(2)}</span>
                            {u.total_earned ? <> &middot; Earned: <span className="font-mono">${Number(u.total_earned || 0).toFixed(2)}</span></> : null}
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <Button variant="secondary" size="sm" className="h-8 gap-1 rounded-full text-[11px] text-[#c8ff00] hover:bg-[#c8ff00]/10 sm:text-xs" data-testid={`mod-user-details-${u.id}`}
                          onClick={() => loadSelectedUser(u.id)}>
                          <Eye size={11} /> Details
                        </Button>
                        <Button variant="secondary" size="sm" className="h-7 gap-1 rounded-full text-[10px] hover:bg-muted/60 sm:text-xs" data-testid={`mod-user-wallet-${u.id}`}
                          onClick={() => { setWalletUserId(u.id); navigate("/mod/wallet"); }}>
                          <Wallet size={11} /> Wallet
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                {!users.length && <div className="py-6 text-center text-xs text-muted-foreground sm:py-8 sm:text-sm">No users found.</div>}
              </CardContent>
            </Card>

            {/* User detail panel */}
            <Card className="border-border/60 bg-card/70">
              <CardHeader className="flex flex-row items-center justify-between gap-2">
                <CardTitle className="text-sm sm:text-base">User Details</CardTitle>
                {selectedUserId && (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="secondary"
                      size="sm"
                      className="h-8 w-8 p-0 rounded-full"
                      data-testid="mod-user-prev"
                      disabled={selectedUserIndex <= 0}
                      onClick={goToPrevUser}
                    >
                      <ChevronLeft size={14} />
                    </Button>
                    <span className="text-[10px] text-muted-foreground tabular-nums sm:text-xs">
                      {selectedUserIndex + 1}/{users.length}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="h-8 w-8 p-0 rounded-full"
                      data-testid="mod-user-next"
                      disabled={selectedUserIndex >= users.length - 1}
                      onClick={goToNextUser}
                    >
                      <ChevronRight size={14} />
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                {!selectedUserId && (
                  <div data-testid="mod-user-details-empty" className="py-6 text-center text-xs text-muted-foreground sm:text-sm">
                    Select a user to view details.
                  </div>
                )}

                {selectedUserProfile && (
                  <div data-testid="mod-user-profile" className="space-y-3">
                    {/* Profile */}
                    <div className="rounded-2xl border border-[#c8ff00]/20 bg-[#c8ff00]/[0.04] p-4">
                      <UserMention testId="mod-selected-user-mention" user={selectedUserProfile.user} meta={selectedUserProfile.user?.discord_id ? `Discord ID: ...${String(selectedUserProfile.user.discord_id).slice(-4)}` : "No Discord ID"} />
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {(selectedUserProfile.user?.roles || [selectedUserProfile.user?.role]).filter(Boolean).map((r) => <Badge key={r} data-testid={`mod-selected-user-role-${r}`} className="bg-[#c8ff00]/10 text-[#c8ff00] capitalize">{r.replace(/_/g, " ")}</Badge>)}
                      </div>
                    </div>

                    {/* Wallet with All-Time Earnings */}
                    <div className="rounded-xl border border-border/60 bg-background p-3 sm:p-4">
                      <div className="text-xs font-semibold sm:text-sm">Wallet</div>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04] p-2">
                          <div className="text-[10px] text-muted-foreground sm:text-xs">All-Time Earnings</div>
                          <div data-testid="mod-profile-all-time-earned" className="mt-0.5 text-xs font-bold text-emerald-600 dark:text-emerald-400 sm:text-sm">${Number(selectedUserProfile.total_earned || 0).toFixed(2)}</div>
                        </div>
                        <div className="rounded-lg border border-border/60 bg-card/70 p-2">
                          <div className="text-[10px] text-muted-foreground sm:text-xs">Available</div>
                          <div className="mt-0.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400 sm:text-sm">${Number(selectedUserProfile.wallet?.available_balance || 0).toFixed(2)}</div>
                        </div>
                        <div className="rounded-lg border border-border/60 bg-card/70 p-2">
                          <div className="text-[10px] text-muted-foreground sm:text-xs">Pending</div>
                          <div className="mt-0.5 text-xs font-semibold text-amber-600 dark:text-amber-400 sm:text-sm">${Number(selectedUserDashboard?.summary?.pending_balance || 0).toFixed(2)}</div>
                        </div>
                        <div className="rounded-lg border border-border/60 bg-card/70 p-2">
                          <div className="text-[10px] text-muted-foreground sm:text-xs">On Hold</div>
                          <div className="mt-0.5 text-xs font-semibold text-rose-600 dark:text-rose-400 sm:text-sm">${Number(selectedUserDashboard?.summary?.on_hold_balance || 0).toFixed(2)}</div>
                        </div>
                      </div>
                    </div>

                    {/* Dashboard stats */}
                    {selectedUserDashboard && (
                      <div className="rounded-xl border border-border/60 bg-background p-3 sm:p-4">
                        <div className="text-xs font-semibold sm:text-sm">Stats</div>
                        <div className="mt-2 grid grid-cols-3 gap-2">
                          <div className="rounded-lg border border-border/60 bg-card/70 p-2 text-center">
                            <div className="text-[10px] text-muted-foreground sm:text-xs">Videos</div>
                            <div className="text-xs font-bold sm:text-sm">{selectedUserDashboard.summary?.total_videos || 0}</div>
                          </div>
                          <div className="rounded-lg border border-border/60 bg-card/70 p-2 text-center">
                            <div className="text-[10px] text-muted-foreground sm:text-xs">Views</div>
                            <div className="text-xs font-bold sm:text-sm">{(selectedUserDashboard.summary?.total_views || 0).toLocaleString()}</div>
                          </div>
                          <div className="rounded-lg border border-border/60 bg-card/70 p-2 text-center">
                            <div className="text-[10px] text-muted-foreground sm:text-xs">Revenue</div>
                            <div className="text-xs font-bold sm:text-sm">${selectedUserDashboard.summary?.total_revenue || 0}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="rounded-xl border border-border/60 bg-background p-3 sm:p-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs font-semibold sm:text-sm">Moderation History</div>
                        <Badge data-testid="mod-profile-duplicate-rate" className="bg-amber-500/10 text-amber-300">Duplicate Rate: {Number(selectedUserProfile.duplicate_rate || 0).toFixed(1)}%</Badge>
                      </div>
                      <div className="mt-2 max-h-[180px] space-y-1.5 overflow-y-auto pr-1">
                        {(selectedUserProfile.moderation_history || []).map((h, i) => (
                          <div key={h.id || i} data-testid={`mod-profile-history-${i}`} className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-card/70 px-3 py-2 text-xs">
                            <span className="capitalize text-white">{String(h.action || "Action").replace(/_/g, " ")}</span>
                            <span className="text-zinc-500">{h.created_at ? new Date(h.created_at).toLocaleString() : "—"}</span>
                          </div>
                        ))}
                        {!(selectedUserProfile.moderation_history || []).length && <div data-testid="mod-profile-history-empty" className="py-3 text-center text-xs text-zinc-500">No moderation history yet.</div>}
                      </div>
                    </div>

                    {/* Payment methods */}
                    <div className="rounded-xl border border-border/60 bg-background p-3 sm:p-4">
                      <div className="text-xs font-semibold sm:text-sm">Payment Methods</div>
                      <div className="mt-2 space-y-1.5">
                        {(selectedUserProfile.user?.payment_methods || []).map((pm) => (
                          <div key={pm.id} className="flex items-center justify-between rounded-lg border border-border/60 bg-card/70 px-3 py-1.5">
                            <div>
                              <div className="text-[10px] font-medium sm:text-xs">{pm.label || pm.type}</div>
                              <div className="text-[10px] text-muted-foreground sm:text-xs">{pm.value}</div>
                            </div>
                            <Badge className="bg-muted text-[10px] text-foreground hover:bg-muted/80">{pm.type}</Badge>
                          </div>
                        ))}
                        {!(selectedUserProfile.user?.payment_methods || []).length && (
                          <div className="text-[10px] text-muted-foreground sm:text-xs">No payment methods.</div>
                        )}
                      </div>
                    </div>

                    {/* Social accounts */}
                    <div className="rounded-xl border border-border/60 bg-background p-3 sm:p-4">
                      <div className="text-xs font-semibold sm:text-sm">Social Accounts</div>
                      <div className="mt-2 space-y-1.5">
                        {(selectedUserProfile.user?.socials || []).map((s) => (
                          <div key={s.id} className="rounded-lg border border-border/60 bg-card/70 p-2">
                            <div className="text-[10px] font-medium sm:text-xs">{s.label}</div>
                            <a className="block truncate text-[10px] text-cyan-400 underline-offset-4 hover:underline sm:text-xs" href={s.url} target="_blank" rel="noreferrer">{s.url}</a>
                            <div className="mt-0.5 text-[10px] text-muted-foreground">{s.status}{s.verification?.status ? ` / ${s.verification.status}` : ""}</div>
                          </div>
                        ))}
                        {!(selectedUserProfile.user?.socials || []).length && (
                          <div className="text-[10px] text-muted-foreground sm:text-xs">No socials.</div>
                        )}
                      </div>
                    </div>

                    {/* Quick wallet action */}
                    <Button variant="secondary" size="sm" className="w-full gap-1.5 rounded-full text-xs" data-testid="mod-user-go-wallet"
                      onClick={() => { setWalletUserId(selectedUserId); navigate("/mod/wallet"); }}>
                      <Wallet size={12} /> Adjust wallet for this user
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
      )}

      {page === "wallet" && (
          <Card className="border-border/60 bg-card/70">
            <CardHeader>
              <CardTitle className="text-sm sm:text-base">Wallet Tools</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Step 1 - Pick user */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold">1. Select User</Label>
                <Select value={walletUserId} onValueChange={(v) => { setWalletUserId(v); loadModTxLogs(v); }}>
                  <SelectTrigger data-testid="mod-wallet-user-select" className="h-10 w-full border-border/60 bg-background text-xs sm:text-sm">
                    <SelectValue placeholder="Choose a user..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[250px]">
                    {(users || []).map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        @{u.discord_username} — ${Number(u.wallet_available || 0).toFixed(2)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Selected user info */}
              {selectedWalletUser && (
                <div className="rounded-xl border border-violet-500/20 bg-violet-500/[0.04] p-3 sm:p-4">
                  <div className="flex items-center justify-between gap-3">
                    <UserMention testId="mod-wallet-selected-user" user={selectedWalletUser} meta="Selected for adjustment" />
                    <div className="text-right">
                      <div className="font-mono text-sm font-bold text-emerald-600 dark:text-emerald-400 sm:text-base">${Number(selectedWalletUser.wallet_available || 0).toFixed(2)}</div>
                      <div className="text-[10px] text-muted-foreground sm:text-xs">available</div>
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04] p-2">
                      <div className="text-[10px] text-muted-foreground sm:text-xs">Available</div>
                      <div className="mt-0.5 text-xs font-bold text-emerald-600 dark:text-emerald-400 sm:text-sm">${Number(selectedWalletUser.wallet_available || 0).toFixed(2)}</div>
                    </div>
                    <div className="rounded-lg border border-amber-500/20 bg-amber-500/[0.04] p-2">
                      <div className="text-[10px] text-muted-foreground sm:text-xs">Pending</div>
                      <div className="mt-0.5 text-xs font-bold text-amber-600 dark:text-amber-400 sm:text-sm">${Number(selectedWalletUser.wallet_pending || 0).toFixed(2)}</div>
                    </div>
                    <div className="rounded-lg border border-rose-500/20 bg-rose-500/[0.04] p-2">
                      <div className="text-[10px] text-muted-foreground sm:text-xs">On Hold</div>
                      <div className="mt-0.5 text-xs font-bold text-rose-600 dark:text-rose-400 sm:text-sm">${Number(selectedWalletUser.wallet_on_hold || 0).toFixed(2)}</div>
                    </div>
                    <div className="rounded-lg border border-border/60 bg-card/70 p-2">
                      <div className="text-[10px] text-muted-foreground sm:text-xs">All-Time</div>
                      <div className="mt-0.5 text-xs font-bold text-foreground sm:text-sm">${Number(selectedWalletUser.total_earned || 0).toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2 - Adjustment form */}
              <div className="space-y-3">
                <Label className="text-xs font-semibold">2. Adjustment</Label>
                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground sm:text-xs">Amount ($)</Label>
                      <Input data-testid="mod-wallet-amount" value={walletAmount} onChange={(e) => setWalletAmount(e.target.value)} placeholder="e.g. 10" className="h-9 border-border/60 bg-background text-xs sm:text-sm" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground sm:text-xs">Type</Label>
                      <Select value={walletType} onValueChange={setWalletType}>
                        <SelectTrigger data-testid="mod-wallet-type" className="h-9 border-border/60 bg-background text-xs sm:text-sm">
                          <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="deposit">Deposit</SelectItem>
                          <SelectItem value="withdraw">Withdraw</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground sm:text-xs">Note (optional)</Label>
                    <Input data-testid="mod-wallet-note" value={walletNote} onChange={(e) => setWalletNote(e.target.value)} placeholder="Reason" className="h-9 border-border/60 bg-background text-xs sm:text-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground sm:text-xs">Tag</Label>
                    <Select value={walletTag || ""} onValueChange={setWalletTag}>
                      <SelectTrigger data-testid="mod-wallet-tag" className="h-9 border-border/60 bg-background text-xs sm:text-sm">
                        <SelectValue placeholder="Select tag (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="campaign_payout">Campaign Payout</SelectItem>
                        <SelectItem value="bonus">Bonus</SelectItem>
                        <SelectItem value="adjustment">Adjustment</SelectItem>
                        <SelectItem value="withdrawal">Withdrawal</SelectItem>
                        <SelectItem value="refund">Refund</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Step 3 - Actions */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold">3. Apply</Label>
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                  <Button data-testid="mod-wallet-apply" variant="secondary" size="sm" className="h-9 gap-1.5 rounded-full text-xs sm:text-sm" disabled={!walletUserId || !walletAmount}
                    onClick={async () => {
                      try {
                        await api.post(`/admin/wallet/${walletUserId}/adjust`, { type: walletType, amount: Number(walletAmount), note: walletNote, tag: walletTag });
                        toast({ title: "Done", description: "Wallet adjusted" });
                        setWalletAmount(""); setWalletNote(""); setWalletTag(""); loadModTxLogs(walletUserId); await loadUsers();
                      } catch (e) { toast({ title: "Error", description: apiErrorMessage(e) }); }
                    }}>
                    Apply {walletType}
                  </Button>
                  <Button data-testid="mod-wallet-move" size="sm" className="h-9 gap-1.5 rounded-full bg-[#c8ff00] text-xs font-bold text-black hover:bg-[#b8ef00] active:scale-[0.97] sm:text-sm" disabled={!walletUserId || !walletAmount}
                    onClick={async () => {
                      try {
                        await api.post(`/admin/wallet/${walletUserId}/move_pending_to_available`, { amount: Number(walletAmount), note: walletNote });
                        toast({ title: "Moved", description: "Pending moved to available" });
                        setWalletAmount(""); setWalletNote(""); await loadUsers();
                      } catch (e) { toast({ title: "Error", description: apiErrorMessage(e) }); }
                    }}>
                    <ArrowRightLeft size={12} /> Move pending to available
                  </Button>
                </div>
              </div>

              {/* Transaction Logs */}
              {walletUserId && modTxLogs.length > 0 && (
                <div className="space-y-3 border-t border-border/60 pt-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold">Transaction History</Label>
                    <span className="text-[10px] text-muted-foreground">{modTxLogs.length} records</span>
                  </div>
                  <div className="max-h-[300px] space-y-2 overflow-y-auto pr-1">
                    {modTxLogs.map((tx, i) => (
                      <div key={tx.id || i} data-testid={`mod-tx-log-${i}`} className="rounded-lg border border-border/60 bg-card/70 p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                              tx.type === "deposit" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" :
                              tx.type === "withdraw" ? "bg-rose-500/15 text-rose-700 dark:text-rose-400" :
                              "bg-violet-500/15 text-violet-700 dark:text-violet-400"
                            }`}>
                              {tx.type}
                            </span>
                            {tx.tag && (
                              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">{tx.tag.replace(/_/g, " ")}</span>
                            )}
                          </div>
                          <span className={`font-mono text-sm font-bold ${tx.amount >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                            {tx.amount >= 0 ? "+" : ""}${Math.abs(tx.amount).toFixed(2)}
                          </span>
                        </div>
                        {tx.note && <div className="mt-1 text-xs text-muted-foreground">{tx.note}</div>}
                        <div className="mt-1 text-[10px] text-muted-foreground/60">
                          {tx.created_at ? new Date(tx.created_at).toLocaleString() : "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
      )}

      {page === "templates" && (
        <div data-testid="mod-templates-page" className="grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
          <Card className="border-[#1a1a1a] bg-[#0a0a0a]">
            <CardHeader><CardTitle className="text-sm">Create Rejection Template</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5"><Label className="text-xs">Title</Label><Input data-testid="mod-template-title" value={templateTitle} onChange={(e) => setTemplateTitle(e.target.value)} className="border-[#1a1a1a] bg-black" placeholder="e.g. Old upload" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Primary Reason</Label><Input data-testid="mod-template-reason" value={templateReason} onChange={(e) => setTemplateReason(e.target.value)} className="border-[#1a1a1a] bg-black" placeholder="Reason shown to moderators" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Message</Label><Textarea data-testid="mod-template-message" value={templateMessage} onChange={(e) => setTemplateMessage(e.target.value)} className="min-h-[110px] border-[#1a1a1a] bg-black" placeholder="Reusable reviewer note" /></div>
              <Button data-testid="mod-template-save" className="w-full rounded-full bg-[#c8ff00] font-bold text-black hover:bg-[#b8ef00]" disabled={!templateTitle.trim() || !templateReason.trim()} onClick={createTemplate}><Sparkles size={14} /> Save Template</Button>
            </CardContent>
          </Card>
          <Card className="border-[#1a1a1a] bg-[#0a0a0a]">
            <CardHeader className="flex flex-row items-center justify-between"><CardTitle className="text-sm">Template Library</CardTitle><Button data-testid="mod-template-refresh" size="sm" variant="secondary" className="rounded-full" onClick={loadTemplates}><RefreshCw size={13} /></Button></CardHeader>
            <CardContent className="space-y-2">
              {(templates || []).map((t) => <div key={t.id} data-testid={`mod-template-card-${t.id}`} className="rounded-2xl border border-[#1a1a1a] bg-black p-4"><div className="flex flex-wrap items-center gap-2"><Badge className="bg-[#c8ff00]/10 text-[#c8ff00]">{t.primary_reason}</Badge><div className="text-sm font-bold text-white">{t.title}</div></div><div className="mt-2 text-sm text-zinc-500">{t.message || "No message"}</div>{t.secondary_reasons?.length ? <div className="mt-2 flex flex-wrap gap-1">{t.secondary_reasons.map((r) => <Badge key={r} className="bg-muted text-foreground">{r}</Badge>)}</div> : null}</div>)}
              {!templates.length && <div data-testid="mod-template-empty" className="py-10 text-center text-sm text-zinc-500">No templates saved yet.</div>}
            </CardContent>
          </Card>
        </div>
      )}

      {page === "bulk" && (
        <Card data-testid="mod-bulk-page" className="border-[#1a1a1a] bg-[#0a0a0a]">
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-sm">Bulk Moderation</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button data-testid="mod-bulk-accept" size="sm" className="rounded-full bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/20" disabled={!selectedSubmissionIds.length || busy} onClick={() => bulkUpdate("accepted")}><Check size={13} /> Accept</Button>
              <Button data-testid="mod-bulk-flag" size="sm" variant="secondary" className="rounded-full bg-amber-500/15 text-amber-300 hover:bg-amber-500/20" disabled={!selectedSubmissionIds.length || busy} onClick={() => bulkUpdate("flagged")}><Flag size={13} /> Flag</Button>
              <Button data-testid="mod-bulk-delete" size="sm" variant="secondary" className="rounded-full bg-rose-500/15 text-rose-300 hover:bg-rose-500/20" disabled={!selectedSubmissionIds.length || busy} onClick={bulkDelete}><Trash2 size={13} /> Delete</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <div data-testid="mod-bulk-selected-count" className="text-xs text-zinc-500">{selectedSubmissionIds.length} selected</div>
            {(submissions || []).map((s) => {
              const checked = selectedSubmissionIds.includes(s.id);
              return (
                <div key={s.id} data-testid={`mod-bulk-row-${s.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#1a1a1a] bg-black p-4">
                  <Checkbox data-testid={`mod-bulk-checkbox-${s.id}`} checked={checked} onCheckedChange={(v) => setSelectedSubmissionIds((prev) => v ? [...new Set([...prev, s.id])] : prev.filter((id) => id !== s.id))} />
                  <StatusBadge status={s.status} />
                  <UserMention testId={`mod-bulk-user-${s.id}`} name={s.clipper_username || s.clipper_tag} avatar={s.clipper_avatar} meta={s.campaign_name || "Campaign"} />
                  <div className="ml-auto text-right text-xs"><div className="font-bold text-white">{Number(s.views || 0).toLocaleString()} views</div><a data-testid={`mod-bulk-open-${s.id}`} href={s.video_url} target="_blank" rel="noreferrer" className="text-[#c8ff00] hover:underline">Open clip</a></div>
                </div>
              );
            })}
            {!submissions.length && <div data-testid="mod-bulk-empty" className="py-10 text-center text-sm text-zinc-500">No submissions available for bulk review.</div>}
          </CardContent>
        </Card>
      )}

      {page === "announcements" && (
          <div className="space-y-4">
            {/* Compose */}
            <Card className="border-border/60 bg-card/70">
              <CardHeader>
                <CardTitle className="text-sm sm:text-base">New Announcement</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Title</Label>
                    <Input data-testid="mod-announce-title" value={announceTitle} onChange={(e) => setAnnounceTitle(e.target.value)} placeholder="Announcement title" className="h-9 border-border/60 bg-background text-xs sm:text-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Message</Label>
                    <Textarea data-testid="mod-announce-message" value={announceMessage} onChange={(e) => setAnnounceMessage(e.target.value)} placeholder="Write your announcement" className="min-h-[100px] border-border/60 bg-background text-xs sm:text-sm" />
                  </div>
                  <Button data-testid="mod-announce-send" size="sm" className="h-9 w-full gap-2 rounded-full bg-[#c8ff00] text-xs font-bold text-black hover:bg-[#b8ef00] active:scale-[0.97] sm:text-sm" disabled={!announceTitle || !announceMessage}
                    onClick={async () => {
                      try {
                        await api.post("/admin/announcements", { scope: "all", title: announceTitle, message: announceMessage, user_ids: [] });
                        setAnnounceTitle(""); setAnnounceMessage(""); await loadAnnouncements();
                        toast({ title: "Sent", description: "Announcement created" });
                      } catch (e) { toast({ title: "Error", description: apiErrorMessage(e) }); }
                    }}>
                    <Send size={13} /> Send to all
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* History */}
            <Card className="border-border/60 bg-card/70">
              <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                <CardTitle className="text-sm sm:text-base">Recent Announcements</CardTitle>
                <Button variant="secondary" size="sm" className="h-8 w-fit gap-1 rounded-full text-xs sm:h-9" data-testid="mod-announce-refresh" onClick={loadAnnouncements}>
                  <RefreshCw size={12} /> Refresh
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(announcements || []).map((a) => (
                    <div key={a.id} data-testid={`mod-announce-row-${a.id}`} className="rounded-xl border border-border/60 bg-background p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-xs font-medium sm:text-sm">{a.title}</div>
                          <div className="mt-0.5 text-[10px] text-muted-foreground line-clamp-2 sm:text-xs">{a.message}</div>
                        </div>
                        <div className="shrink-0 text-[10px] text-muted-foreground sm:text-xs">
                          {a.created_at ? new Date(a.created_at).toLocaleDateString() : "-"}
                        </div>
                      </div>
                    </div>
                  ))}
                  {!announcements?.length && <div data-testid="mod-announce-empty" className="py-6 text-center text-xs text-muted-foreground sm:text-sm">No announcements yet.</div>}
                </div>
              </CardContent>
            </Card>
          </div>
      )}
    </div>
  );
}
